import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, authHeaders, describeError } from "../lib/api";
import type { Incident, IncidentStatus } from "../lib/api";
import { shortId } from "../lib/format";
import {
  INCIDENT_ACTION_LABEL,
  INCIDENT_CATEGORY_LABEL,
  INCIDENT_STATUS_LABEL,
  dateTimeLo,
  formatCoords,
  incidentStatusBadge,
  mapsUrl,
  placeLabel,
} from "../lib/services";
import { AlertTriangle, ChevronLeft, ExternalLink, MapPin, Phone, X } from "../components/icons";

const MAX_NOTE = 1000;

/** เส้นทางหลักของเรื่อง — rejected แสดงแยก (ปิดเรื่องโดยไม่รับ) */
const MAIN_PATH: IncidentStatus[] = ["new", "acknowledged", "in_progress", "resolved"];

class ConflictError extends Error {}

async function fetchIncident(id: string): Promise<Incident> {
  const { data, error, response } = await api.GET("/v1/admin/incidents/{id}", {
    params: { path: { id } },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data;
}

export function IncidentDetailPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const detail = useQuery({ queryKey: ["incidents", "detail", id], queryFn: () => fetchIncident(id), enabled: !!id });
  // null = ยังไม่ได้แก้ ใช้บันทึกเดิมของเจ้าหน้าที่ (ส่งสถานะเดิม = แก้แค่บันทึก)
  const [noteDraft, setNoteDraft] = useState<string | null>(null);
  const [zoom, setZoom] = useState<string | null>(null);

  const note = noteDraft ?? detail.data?.officer_note ?? "";

  const update = useMutation<Incident, Error, IncidentStatus>({
    mutationFn: async (status) => {
      const inc = detail.data;
      if (!inc) throw new Error("ບໍ່ພົບເລື່ອງນີ້");
      const { data, error, response } = await api.PATCH("/v1/admin/incidents/{id}", {
        params: { path: { id: inc.id } },
        body: { status, officer_note: note.trim(), version: inc.version },
        headers: authHeaders(),
      });
      if (response.status === 409) throw new ConflictError("ມີຄົນອື່ນແກ້ເລື່ອງນີ້ກ່ອນແລ້ວ — ໂຫຼດຂໍ້ມູນລ່າສຸດໃຫ້ແລ້ວ ກວດແລ້ວລອງໃໝ່");
      if (error || !data) throw new Error(describeError(error, response.status));
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(["incidents", "detail", id], data);
      setNoteDraft(null);
      void qc.invalidateQueries({ queryKey: ["incidents", "admin"] });
    },
    onError: (e) => {
      if (e instanceof ConflictError) void detail.refetch();
    },
  });

  useEffect(() => {
    if (!zoom) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setZoom(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom]);

  if (detail.isPending) {
    return (
      <div>
        <BackLink />
        <p className="caption" style={{ marginTop: "var(--space-4)" }}>
          ກຳລັງໂຫຼດ…
        </p>
      </div>
    );
  }
  if (detail.isError) {
    return (
      <div>
        <BackLink />
        <div className="alert-error" style={{ marginTop: "var(--space-4)" }}>
          ໂຫຼດເລື່ອງແຈ້ງບໍ່ສຳເລັດ: {detail.error.message}
        </div>
      </div>
    );
  }

  const inc = detail.data;
  const hasCoords = inc.lat !== null && inc.lng !== null;
  const closed = inc.status === "resolved" || inc.status === "rejected";
  const currentStep = MAIN_PATH.indexOf(inc.status);

  return (
    <div>
      <BackLink />
      <header className="page-header" style={{ marginTop: "var(--space-3)" }}>
        <div>
          <h1 className="page-header__title">{INCIDENT_CATEGORY_LABEL[inc.category]}</h1>
          <p className="page-header__subtitle">
            #<span className="num">{shortId(inc.id)}</span> · ແຈ້ງເມື່ອ <span className="num">{dateTimeLo(inc.created_at)}</span>
          </p>
        </div>
        <span className={incidentStatusBadge(inc.status)}>{INCIDENT_STATUS_LABEL[inc.status]}</span>
      </header>

      <div className="grid2">
        <div className="stack">
          <section className="card">
            <h2 className="section-title">ລາຍລະອຽດ</h2>
            <p className="body-sm incident-desc" style={{ marginTop: "var(--space-2)" }}>
              {inc.description}
            </p>
            {inc.photo_urls.length > 0 ? (
              <ul className="incident-photos">
                {inc.photo_urls.map((url, i) => (
                  <li key={url}>
                    <button
                      type="button"
                      className="incident-photo"
                      onClick={() => setZoom(url)}
                      aria-label={`ເບິ່ງຮູບທີ ${String(i + 1)}`}
                    >
                      <img src={url} alt="" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="caption" style={{ marginTop: "var(--space-3)" }}>
                ບໍ່ມີຮູບປະກອບ
              </p>
            )}
          </section>

          <section className="card">
            <h2 className="section-title">ສະຖານທີ່</h2>
            <dl className="incident-facts">
              <dt>ບ້ານທີ່ຮັບເລື່ອງ</dt>
              <dd>{placeLabel(inc)}</dd>
              {inc.province_name_lo && (
                <>
                  <dt>ແຂວງ</dt>
                  <dd>{inc.province_name_lo}</dd>
                </>
              )}
              <dt>ພິກັດ (ຈາກເຄື່ອງຜູ້ແຈ້ງ)</dt>
              <dd className="num">{hasCoords ? formatCoords(inc.lat ?? 0, inc.lng ?? 0, inc.accuracy_m) : "ບໍ່ໄດ້ສົ່ງພິກັດມາ"}</dd>
            </dl>
            {hasCoords && (
              <a
                className="btn-outline"
                style={{ display: "inline-flex", marginTop: "var(--space-3)" }}
                href={mapsUrl(inc.lat ?? 0, inc.lng ?? 0)}
                target="_blank"
                rel="noreferrer"
              >
                <MapPin size={14} /> ເປີດແຜນທີ່ <ExternalLink size={12} />
              </a>
            )}
            <p className="caption" style={{ marginTop: "var(--space-2)" }}>
              ພິກັດເປັນຄ່າທີ່ເຄື່ອງຜູ້ແຈ້ງສົ່ງມາ ໃຊ້ຊ່ວຍຊອກສະຖານທີ່ເກີດເຫດເທົ່ານັ້ນ
            </p>
          </section>
        </div>

        <div className="stack">
          <section className="card">
            <h2 className="section-title">ຜູ້ແຈ້ງ</h2>
            {inc.reporter ? (
              <dl className="incident-facts">
                <dt>ຊື່</dt>
                <dd>{inc.reporter.full_name || "—"}</dd>
                <dt>ເບີໂທ</dt>
                <dd>
                  {inc.reporter.phone ? (
                    <a className="num" href={`tel:${inc.reporter.phone}`}>
                      <Phone size={12} /> {inc.reporter.phone}
                    </a>
                  ) : (
                    "—"
                  )}
                </dd>
              </dl>
            ) : (
              <p className="caption" style={{ marginTop: "var(--space-2)" }}>
                ບໍ່ມີຂໍ້ມູນຕິດຕໍ່
              </p>
            )}
          </section>

          <section className="card">
            <h2 className="section-title">ສະຖານະ</h2>
            {inc.status === "rejected" ? (
              <p className="body-sm" style={{ marginTop: "var(--space-2)" }}>
                <span className={incidentStatusBadge("rejected")}>{INCIDENT_STATUS_LABEL.rejected}</span> ປິດເລື່ອງໂດຍບໍ່ຮັບ
              </p>
            ) : (
              <ol className="incident-steps">
                {MAIN_PATH.map((s, i) => (
                  <li key={s} className={i < currentStep ? "is-done" : i === currentStep ? "is-current" : undefined}>
                    {INCIDENT_STATUS_LABEL[s]}
                  </li>
                ))}
              </ol>
            )}
            <p className="caption" style={{ marginTop: "var(--space-2)" }}>
              ອັບເດດລ່າສຸດ <span className="num">{dateTimeLo(inc.updated_at)}</span>
              {inc.resolved_at && (
                <>
                  {" "}
                  · ປິດເລື່ອງ <span className="num">{dateTimeLo(inc.resolved_at)}</span>
                </>
              )}
            </p>
            {inc.officer_note && !inc.can_update && (
              <div style={{ marginTop: "var(--space-3)" }}>
                <div className="form-field-label">ໝາຍເຫດຈາກເຈົ້າໜ້າທີ່</div>
                <p className="body-sm incident-desc">{inc.officer_note}</p>
              </div>
            )}
          </section>

          {inc.can_update && (
            <section className="card">
              <h2 className="section-title">ດຳເນີນການ</h2>
              <div className="stack" style={{ marginTop: "var(--space-3)" }}>
                <div>
                  <label className="form-field-label" htmlFor="incident-note">
                    ໝາຍເຫດເຖິງຜູ້ແຈ້ງ (ບໍ່ບັງຄັບ)
                  </label>
                  <textarea
                    id="incident-note"
                    className="input-field"
                    maxLength={MAX_NOTE}
                    value={note}
                    onChange={(e) => setNoteDraft(e.target.value)}
                  />
                  <p className="caption">ຜູ້ແຈ້ງເຫັນໝາຍເຫດນີ້ໃນແອັບ</p>
                </div>
                {inc.next_statuses.length > 0 ? (
                  <div className="row" style={{ flexWrap: "wrap" }}>
                    {inc.next_statuses.map((s, i) => (
                      <button
                        key={s}
                        type="button"
                        className={s === "rejected" ? "btn-danger" : i === 0 ? "btn-primary" : "btn-secondary"}
                        disabled={update.isPending}
                        onClick={() => update.mutate(s)}
                      >
                        {s === "rejected" && <X size={14} />} {INCIDENT_ACTION_LABEL[s]}
                      </button>
                    ))}
                    {!closed && note.trim() !== inc.officer_note.trim() && (
                      <button
                        type="button"
                        className="btn-outline"
                        disabled={update.isPending}
                        onClick={() => update.mutate(inc.status)}
                      >
                        ບັນທຶກໝາຍເຫດ
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="caption">ເລື່ອງນີ້ປິດແລ້ວ</p>
                )}
                {update.isError && (
                  <p className="field-error" role="alert">
                    <AlertTriangle size={14} /> {update.error.message}
                  </p>
                )}
              </div>
            </section>
          )}
        </div>
      </div>

      {zoom && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="ຮູບປະກອບ" onClick={() => setZoom(null)}>
          <div className="incident-lightbox">
            <img src={zoom} alt="ຮູບປະກອບເລື່ອງແຈ້ງ" />
            <button type="button" className="btn-secondary" onClick={() => setZoom(null)}>
              <X size={14} /> ປິດ
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/incidents" className="btn-outline" style={{ display: "inline-flex" }}>
      <ChevronLeft size={14} /> ກັບລາຍການແຈ້ງເຫດ
    </Link>
  );
}
